import { routeNameFromPathname } from "./route-map"
import { isMeTabContext } from "~ui/app-shell"

export type PcPresentationType = "center" | "drawer" | "bottom"

export type PcPresentationOptions = {
  /** 居中弹窗高度，默认 70vh */
  height?: string
}

/** PC 居中弹窗默认高度（相对详情区，不超过 70vh） */
export const PC_CENTER_PANEL_HEIGHT = "min(70vh, calc(100% - 48px))"

/** PC 详情区路由展示模式（非全页替换） */
export const PC_SCREEN_PRESENTATION: Record<string, PcPresentationType> = {
  AddContact: "center",
  AddGroupChat: "center",
  AddChannel: "center",
  ChannelList: "center",
  GroupList: "center",
  FriendList: "center",
  GroupChatManagement: "drawer",
  GroupChatInformation: "drawer",
  ChannelChatManagement: "drawer",
  ChannelChatInformation: "drawer",
  PersonalCard: "drawer",
}

/** 「我的」子页弹窗标题（无动态 params 时的兜底） */
export const ME_PRESENTATION_TITLES: Record<string, string> = {
  Setting: "设置",
  BasicInfo: "个人信息",
  Avatar: "个人头像",
  MyAccount: "我的账号",
  PrivacyAndSafety: "隐私安全",
  SecurityGoogle: "启用谷歌验证器",
  GroupAssistant: "群发助手",
  NotDisturbMode: "免打扰",
  NotificationBell: "通知铃声",
  LanguageSettings: "语言",
  FontsizeSettings: "字体大小",
  LoginDevice: "登录设备",
  ClearCacheData: "清除缓存",
  OnlineTimeSettings: "上线时间",
  SelfDestructTimeSettings: "消息自毁",
  AddMethodSettings: "添加方式",
  BlackList: "黑名单",
  UpdateCurrentUserEmail: "绑定邮箱",
  UpdateCurrentUserGender: "性别",
}

/** 居中弹窗内不再重复 Shell 顶栏的路由 */
export const ME_PRESENTATION_NO_HEADER = new Set(["UpdateCurrentUserGender"])

/** 各路由浮层附加样式（未配置时使用 PC_CENTER_PANEL_HEIGHT） */
export const PC_SCREEN_PRESENTATION_OPTIONS: Record<string, PcPresentationOptions> = {
  ChannelList: { height: PC_CENTER_PANEL_HEIGHT },
  GroupList: { height: PC_CENTER_PANEL_HEIGHT },
  FriendList: { height: PC_CENTER_PANEL_HEIGHT },
}

export function getPcPresentation(routeName: string | null | undefined): PcPresentationType | null {
  if (!routeName) return null
  return PC_SCREEN_PRESENTATION[routeName] ?? null
}

export function getPcPresentationOptions(
  routeName: string | null | undefined,
): PcPresentationOptions | null {
  if (!routeName) return null
  return PC_SCREEN_PRESENTATION_OPTIONS[routeName] ?? null
}

export function getPcPresentationForPath(pathname: string): PcPresentationType | null {
  const routeName = routeNameFromPathname(pathname)
  if (routeName) {
    const explicit = PC_SCREEN_PRESENTATION[routeName]
    if (explicit) return explicit
  }
  if (pathname !== "/me" && isMeTabContext(pathname)) {
    return "center"
  }
  return null
}

export function getMePresentationTitle(
  routeName: string | null | undefined,
  params?: Record<string, unknown>,
): string | undefined {
  if (!routeName) return undefined
  const paramTitle = params?.title
  if (typeof paramTitle === "string" && paramTitle.trim()) return paramTitle
  return ME_PRESENTATION_TITLES[routeName]
}

export function shouldShowMePresentationHeader(routeName: string | null | undefined) {
  if (!routeName) return true
  return !ME_PRESENTATION_NO_HEADER.has(routeName)
}

/**
 * PopupProvider / 详情区 scope：聊天室上叠抽屉时保持同一 key，避免 PcDetailOutlet 重挂载丢失底层聊天。
 */
export function getPcDetailScopeKey(listPath: string, pathname: string, search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  const chatId = params.get("chat_id")
  const presentation = getPcPresentationForPath(pathname)

  if (chatId && (pathname === "/room" || presentation)) {
    return `${listPath}|room:${chatId}|${search}`
  }

  return `${listPath}|${pathname}|${search}`
}
